import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { LinearProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import ColorBlock from "./ColorBlock";
import { Color } from "../../../../core/models/entities/LogoOptions";
import { colorPickerLabel } from "../../../../utils/systemColors";

export interface ResolvedColorChange {
  from: Color;
  to: Color;
}

/**
 * Flag REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW: replaces the modal
 * LoadingElement over the whole editor tool while a mutation (colour change,
 * resize, draft save) is in flight. The current image stays visible; this
 * strip sits over the top of the canvas with a thin indeterminate progress
 * line and, for colour changes, the from -> to chips the user just chose
 * (LogoColorChanger clears its own selection synchronously, so the chips
 * are fed from the view-model's captured list). Buttons stay disabled via
 * the existing isLoading wiring - only the overlay goes.
 */
export function MutationProgress({
  label,
  colorChanges,
  embroideryMethod,
  insetForRulers = false,
}: {
  label: string;
  colorChanges?: ResolvedColorChange[];
  embroideryMethod?: boolean;
  // Standalone editor: the 24px mm rulers sit at the container's top/left -
  // start below/right of them so the strip never hides the ruler the user
  // is watching during a resize.
  insetForRulers?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={
        "absolute right-0 z-20 pointer-events-none flex flex-col " +
        (insetForRulers ? "top-6 left-6" : "top-0 left-0")
      }
      data-testid="mutation-progress"
      role="status"
      aria-live="polite"
    >
      <LinearProgress sx={{ height: 3 }} />
      <div
        className={
          "flex flex-wrap items-center gap-2 px-3 py-1.5 bg-white/90 text-xs text-gray-600"
        }
      >
        <span className={"font-medium"}>{label}</span>
        {colorChanges?.map((c) => (
          <span
            key={`${c.from.id}-${c.to.id}`}
            className={
              "inline-flex items-center space-x-1 border border-gray-200 rounded px-1.5 py-0.5 bg-white"
            }
          >
            <ColorBlock color={c.from} size={"sm"} />
            <span>{colorPickerLabel(c.from, t, { embroideryMethod })}</span>
            <FontAwesomeIcon size={"xs"} icon={faArrowRight} />
            <ColorBlock color={c.to} size={"sm"} />
            <span>{colorPickerLabel(c.to, t, { embroideryMethod })}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
